import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Motiv, ve kterém aplikace běží. `system` se řídí nastavením operačního systému. */
export type ThemeChoice = 'light' | 'dark' | 'system';

export const themeChoices: ThemeChoice[] = ['system', 'light', 'dark'];

/**
 * Světlý a tmavý motiv.
 *
 * Volba se ukládá na dvě místa a každé z nich má svůj úkol. V prohlížeči proto,
 * aby se motiv vykreslil hned při startu — než aplikace stihne zjistit, kdo je
 * přihlášený, už je něco na obrazovce a bílé bliknutí by bylo vidět. Na účtu proto,
 * aby volba přešla i na další zařízení.
 *
 * Po přihlášení rozhoduje účet, před ním zapamatovaná volba prohlížeče.
 *
 * Motiv se nastavuje atributem `data-theme` na `<html>`, protože obě stylovací vrstvy
 * projektu (Ant tokeny v LESS i Bootstrap ve SCSS) se kompilují napevno a za běhu
 * se přepnout nedají. CSS proměnné nad nimi ano.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {

  private static readonly storageKey = 'uiTheme';

  private readonly choiceSubject = new BehaviorSubject<ThemeChoice>(this.remembered ?? 'system');

  /** Co si uživatel zvolil — včetně `system`. */
  get choice$(): Observable<ThemeChoice> {
    return this.choiceSubject.asObservable();
  }

  get choice(): ThemeChoice {
    return this.choiceSubject.value;
  }

  /** Volba zapamatovaná na tomhle počítači, nebo undefined, když tu ještě nikdo nebyl. */
  private get remembered(): ThemeChoice | undefined {
    const stored = localStorage.getItem(ThemeService.storageKey);

    return themeChoices.includes(stored as ThemeChoice) ? (stored as ThemeChoice) : undefined;
  }

  /** Vykreslí motiv podle zapamatované volby. Volá se jednou při startu aplikace. */
  init(): void {
    this.apply(this.choice);

    // Když se uživatel drží systému, musí ho aplikace následovat i za běhu —
    // Windows i macOS umí přepnout na tmavý režim podle času.
    window.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', () => {
        if (this.choice === 'system') {
          this.apply('system');
        }
      });
  }

  /**
   * Nastaví motiv podle hodnoty z účtu. Nepřepisuje ho na server — tohle je čtení
   * toho, co už tam je, ne nová volba uživatele.
   */
  applyFromAccount(theme: string | undefined | null): void {
    const choice = themeChoices.includes(theme as ThemeChoice) ? (theme as ThemeChoice) : 'system';

    localStorage.setItem(ThemeService.storageKey, choice);
    this.choiceSubject.next(choice);
    this.apply(choice);
  }

  use(choice: ThemeChoice): void {
    localStorage.setItem(ThemeService.storageKey, choice);
    this.choiceSubject.next(choice);
    this.apply(choice);
  }

  /** Motiv, který je opravdu vidět — `system` se rozpadne na světlý nebo tmavý. */
  resolved(choice: ThemeChoice = this.choice): 'light' | 'dark' {
    if (choice !== 'system') {
      return choice;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private apply(choice: ThemeChoice): void {
    document.documentElement.setAttribute('data-theme', this.resolved(choice));
  }
}
